<template>
  <div ref="mapContainer" style="width: 100%; height: 400px"></div>
</template>

<script setup lang="ts">
import { ref, watchEffect, onMounted } from 'vue'
import { rdToWgs84 } from '../utils/rdToWgs84'

const props = defineProps<{
  coordX?: number
  coordY?: number
  latitude?: number
  longitude?: number
  address?: string
  coordinateSystem?: 'RD' | 'WGS84'
}>()

const mapContainer = ref(null)
const { GOOGLE_MAPS_API_KEY } = useRuntimeConfig().public
const isClient = ref(false)

onMounted(() => {
  isClient.value = true
})

function loadGoogleMapsAPI(callback: () => void) {

  if (document.querySelector('script[src*="maps.googleapis.com"]')) {
    callback()
    return
  }

  const script = document.createElement("script")
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`
  script.async = true
  script.defer = true
  script.onload = callback
  document.head.appendChild(script)
}

function initializeMap(lat: number, lon: number) {
  const map = new google.maps.Map(mapContainer.value, {
    center: { lat, lng: lon },
    zoom: 15,
  })

  new google.maps.Marker({
    position: { lat, lng: lon },
    map,
  })
}

function geocodeAddress(address: string) {
  const geocoder = new google.maps.Geocoder()
  geocoder.geocode({ address }, (results, status) => {
    if (status === 'OK' && results[0]) {
      const location = results[0].geometry.location
      initializeMap(location.lat(), location.lng())
    } else {
      console.error('Geocoding failed:', status)
    }
  })
}

watchEffect(() => {
  if (isClient.value && mapContainer.value) {
    let lat: number, lon: number

    // Use latitude/longitude if provided
    if (props.latitude && props.longitude) {
      lat = props.latitude
      lon = props.longitude
    }
    // Use coordX/coordY with coordinate system detection
    else if (props.coordX && props.coordY) {
      if (props.coordinateSystem === 'WGS84' || (props.coordX < 180 && props.coordY < 90)) {
        lat = props.coordY
        lon = props.coordX
      } else {
        const converted = rdToWgs84(props.coordX, props.coordY)
        lat = converted.lat
        lon = converted.lon
      }
    }

    if (lat && lon) {
      if (!window.google) {
        loadGoogleMapsAPI(() => initializeMap(lat, lon))
      } else {
        initializeMap(lat, lon)
      }
    } else if (props.address) {
      if (!window.google) {
        loadGoogleMapsAPI(() => geocodeAddress(props.address))
      } else {
        geocodeAddress(props.address)
      }
    }
  }
})
</script>
